import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { TriangleAlertIcon } from '@ttab/elephant-ui/icons'
import type * as Y from 'yjs'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'
import { useArticleHeadline } from '@/hooks/useArticleHeadline'
import { appliesTitleFallback, hasTitleFallback } from '@/lib/applyArticleTitleFallback'

/**
 * Warns that the stored document title will be used as the article title,
 * because the editable headline is empty.
 *
 * The editable headline is the heading-1 node in the document content. When it
 * is empty, the publish transform (makeTitle in yjsNewsDoc) falls back to the
 * stored document title (root.title, seeded from the assignment title for
 * deliverables), and EditorHeader writes that same title into the headline so
 * the editor shows what is stored. Mirror makeTitle's `newHeading || title`
 * exactly - raw falsiness, no trimming - so this warning never disagrees with
 * what is actually published. A whitespace-only headline is truthy, so it is
 * published as-is and no warning shows.
 *
 * Rendered by both status prompts (PromptDefault and PromptSchedule) so the
 * gate and the copy cannot drift apart between them.
 */
export const TitleFallbackWarning = ({ ydoc, documentType, status }: {
  ydoc?: YDocument<Y.Map<unknown>>
  documentType?: string
  status: string
}): JSX.Element | null => {
  const { t } = useTranslation()
  const headline = useArticleHeadline(ydoc)
  // Use the assignment title as fallback, in case the article headline is empty
  const [fallbackTitle] = useYValue<string>(ydoc?.ele, 'root.title')

  const applies = !!ydoc?.ele && hasTitleFallback(documentType)
    && appliesTitleFallback(status)

  if (!applies || headline) {
    return null
  }

  return (
    <div className='flex gap-2'>
      <TriangleAlertIcon size={18} strokeWidth={1.75} className='text-orange-700 dark:text-orange-400' />
      <div className='text-sm text-orange-700 dark:text-orange-400'>
        {fallbackTitle
          ? t('shared:status_menu.titleFallback', { title: fallbackTitle })
          : t('shared:status_menu.titleMissingBoth')}
      </div>
    </div>
  )
}
